/**
 * Scroll Restoration Hook
 *
 * Saves and restores scroll positions across browser history navigations
 * using React Router's location.key and sessionStorage.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll_pos_';
const MAX_ENTRIES = 50;

function saveScrollPosition(key: string) {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(window.scrollY));
  } catch {
    // sessionStorage full or unavailable
  }
}

function getScrollPosition(key: string): number | null {
  try {
    const val = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return val !== null ? Number(val) : null;
  } catch {
    return null;
  }
}

function pruneEntries() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(STORAGE_PREFIX)) keys.push(k);
    }
    if (keys.length > MAX_ENTRIES) {
      const toRemove = keys.slice(0, keys.length - MAX_ENTRIES);
      toRemove.forEach((k) => sessionStorage.removeItem(k));
    }
  } catch {
    // ignore
  }
}

export function useScrollRestoration() {
  const location = useLocation();
  const prevKeyRef = useRef<string>(location.key);

  // Save scroll position of the page being left, restore for the new page
  useEffect(() => {
    const prevKey = prevKeyRef.current;
    const currentKey = location.key;

    // Save position of the page we're leaving
    if (prevKey && prevKey !== currentKey) {
      saveScrollPosition(prevKey);
    }

    // Check for a saved position for the page we're arriving at
    const savedY = getScrollPosition(currentKey);

    if (savedY !== null) {
      // Back/forward navigation: restore position after paint
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedY, behavior: 'instant' });
      });
    } else {
      // New navigation: scroll to top (uses CSS smooth behavior)
      window.scrollTo({ top: 0 });
    }

    prevKeyRef.current = currentKey;
    pruneEntries();
  }, [location.key]);

  // Save scroll position on page unload (survives refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition(location.key);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.key]);
}
